import React, { useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';

const About = () => {
  const timelineRefs = useRef([]);
  const valuesRefs = useRef([]);
  const ctaRef = useRef(null);
  const timelineLineRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  // Timeline scroll progress
  useEffect(() => {
    const handleScroll = () => {
      if (!timelineLineRef.current) return;
      const timelineContainer = document.querySelector('.timeline-container');
      if (!timelineContainer) return;
      const rect = timelineContainer.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const visiblePart = Math.min(1, Math.max(0, (viewportHeight - rect.top) / (rect.height + viewportHeight)));
      timelineLineRef.current.style.height = `${visiblePart * 100}%`;
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Particle background canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initParticles();
    };
    const initParticles = () => {
      particles = [];
      const particleCount = Math.min(100, Math.floor(window.innerWidth / 20));
      for (let i = 0; i < particleCount; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * 2 + 1,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.2,
          alpha: Math.random() * 0.5 + 0.2,
        });
      }
    };
    const draw = () => {
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 217, 102, ${p.alpha})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
      });
      animationId = requestAnimationFrame(draw);
    };
    window.addEventListener('resize', resize);
    resize();
    draw();
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  // Scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.2 }
    );
    timelineRefs.current.forEach((ref) => ref && observer.observe(ref));
    valuesRefs.current.forEach((ref) => ref && observer.observe(ref));
    if (ctaRef.current) observer.observe(ctaRef.current);
    return () => observer.disconnect();
  }, []);

  const addTimelineRef = useCallback((el, index) => {
    if (el && !timelineRefs.current.includes(el)) {
      timelineRefs.current[index] = el;
    }
  }, []);
  const addValueRef = useCallback((el, index) => {
    if (el && !valuesRefs.current.includes(el)) {
      valuesRefs.current[index] = el;
    }
  }, []);

  const timelineItems = [
    { year: '2005', title: 'Humble Beginnings', desc: 'Started with a single truck and a vision for honest septic care.', icon: '🚛' },
    { year: '2010', title: 'Technology Upgrade', desc: 'Introduced camera inspections and expanded our fleet.', icon: '📷' },
    { year: '2018', title: '24/7 Emergency Service', desc: 'Became the first in the region to offer round‑the‑clock support.', icon: '⏰' },
    { year: '2026', title: '2,000+ Happy Homes', desc: 'Reached a milestone of 5,000 satisfied customers and counting.', icon: '🏆' },
  ];

  const values = [
    { icon: '💧', title: 'Quality Craftsmanship', desc: 'Every job is done right the first time with precision and care.' },
    { icon: '🌱', title: 'Eco‑Conscious', desc: 'Our methods protect your property and the environment.' },
    { icon: '🤝', title: 'No‑Surprise Pricing', desc: 'Transparent quotes before we start – no hidden fees.' },
    { icon: '⚡', title: 'Rapid Response', desc: 'Emergency crews ready 24/7 to handle any crisis.' },
  ];

  const deepBlue = '#0A2F44';
  const silver = '#F0F4F8';
  const gold = '#FFD966';

  const styles = {
    pageWrapper: {
      backgroundColor: deepBlue,
      minHeight: '100vh',
      overflowX: 'hidden',
      position: 'relative',
    },
    canvas: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: 0,
    },
    content: {
      position: 'relative',
      zIndex: 1,
    },
    pageHeading: {
      textAlign: 'center',
      paddingTop: '1rem',
      marginBottom: '0',
      fontSize: '3rem',
      fontWeight: 'bold',
      color: '#FFFFFF',
      textShadow: '0 0 8px rgba(240,244,248,0.3)',
      animation: 'fadeInDown 0.8s ease',
    },
    hero: {
      display: 'flex',
      flexWrap: 'wrap',
      minHeight: '90vh',
      alignItems: 'center',
      justifyContent: 'space-between',
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '4rem 1rem',
      position: 'relative',
    },
    heroLeft: {
      flex: '1 1 55%',
      paddingRight: '2rem',
      opacity: 0,
      transform: 'translateX(-30px)',
      transition: 'opacity 0.8s ease, transform 0.8s ease',
      animation: 'fadeInLeft 0.8s forwards',
    },
    heroRight: {
      flex: '1 1 40%',
      opacity: 0,
      transform: 'translateX(30px)',
      transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s',
      animation: 'fadeInRight 0.8s forwards 0.2s',
    },
    heroBadge: {
      display: 'inline-block',
      background: `rgba(255,217,102,0.15)`,
      color: gold,
      padding: '0.25rem 1rem',
      borderRadius: '50px',
      fontSize: '0.85rem',
      fontWeight: 'bold',
      marginBottom: '1rem',
    },
    heroTitle: {
      fontSize: '3rem',
      fontWeight: 'bold',
      color: '#FFFFFF',
      marginBottom: '1rem',
      textShadow: '0 0 8px rgba(240,244,248,0.3)',
    },
    heroText: {
      fontSize: '1rem',
      lineHeight: '1.7',
      color: silver,
      marginBottom: '2rem',
    },
    btnPrimary: {
      display: 'inline-block',
      backgroundColor: gold,
      color: deepBlue,
      fontWeight: 'bold',
      padding: '0.75rem 2rem',
      borderRadius: '50px',
      textDecoration: 'none',
      transition: 'all 0.3s',
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    },
    heroImage: {
      width: '100%',
      maxWidth: '400px',
      borderRadius: '20px',
      boxShadow: '0 20px 30px rgba(0,0,0,0.3)',
      transition: 'transform 0.3s',
      display: 'block',
      margin: '0 auto',
    },
    separator: {
      textAlign: 'center',
      padding: '3rem 1rem',
      maxWidth: '800px',
      margin: '0 auto',
    },
    separatorLine: {
      width: '100px',
      height: '2px',
      background: `linear-gradient(90deg, transparent, ${gold}, transparent)`,
      margin: '0 auto 1.5rem auto',
      animation: 'pulseGlow 2s infinite',
    },
    separatorText: {
      fontSize: '1.2rem',
      color: silver,
      fontStyle: 'italic',
      letterSpacing: '1px',
      fontWeight: '300',
    },
    timelineSection: {
      maxWidth: '1000px',
      margin: '5rem auto',
      padding: '0 1rem',
    },
    sectionTitle: {
      fontSize: '2rem',
      fontWeight: 'bold',
      textAlign: 'center',
      color: '#FFFFFF',
      marginBottom: '3rem',
      position: 'relative',
    },
    timelineContainer: {
      position: 'relative',
      paddingLeft: '2rem',
    },
    timelineLine: {
      position: 'absolute',
      left: '0.5rem',
      top: 0,
      width: '2px',
      background: gold,
      transition: 'height 0.2s linear',
    },
    timelineItem: {
      position: 'relative',
      marginBottom: '3rem',
      opacity: 0,
      transform: 'translateX(-20px)',
      transition: 'opacity 0.6s ease, transform 0.6s ease',
      paddingLeft: '2rem',
    },
    timelineIcon: {
      position: 'absolute',
      left: '-1.8rem',
      top: 0,
      width: '3rem',
      height: '3rem',
      background: deepBlue,
      border: `2px solid ${gold}`,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.2rem',
      color: gold,
      zIndex: 1,
      transition: 'all 0.2s',
    },
    timelineYear: {
      fontSize: '1.3rem',
      fontWeight: 'bold',
      color: gold,
      marginBottom: '0.25rem',
    },
    timelineTitle: {
      fontSize: '1.1rem',
      fontWeight: 'bold',
      color: '#FFFFFF',
      marginBottom: '0.5rem',
    },
    timelineDesc: {
      color: silver,
      lineHeight: '1.5',
      fontSize: '0.95rem',
    },
    valuesSection: {
      maxWidth: '900px',
      margin: '4rem auto',
      padding: '0 1rem',
    },
    valueItem: {
      display: 'flex',
      gap: '1.5rem',
      marginBottom: '2rem',
      padding: '1rem 0',
      borderBottom: '1px solid rgba(255,217,102,0.2)',
      opacity: 0,
      transform: 'translateY(20px)',
      transition: 'opacity 0.6s ease, transform 0.6s ease',
      cursor: 'pointer',
    },
    valueIcon: {
      fontSize: '2rem',
      color: gold,
      flexShrink: 0,
      transition: 'transform 0.2s',
    },
    valueContent: {
      flex: 1,
    },
    valueTitle: {
      fontSize: '1.2rem',
      fontWeight: 'bold',
      color: '#FFFFFF',
      marginBottom: '0.5rem',
    },
    valueDesc: {
      color: silver,
      lineHeight: '1.5',
      fontSize: '0.95rem',
    },
    ctaSection: {
      background: `linear-gradient(135deg, rgba(255,217,102,0.15), rgba(10,47,68,0.9))`,
      borderRadius: '24px',
      maxWidth: '1000px',
      margin: '4rem auto',
      padding: '3rem 2rem',
      textAlign: 'center',
      opacity: 0,
      transform: 'translateY(20px)',
      transition: 'opacity 0.8s ease, transform 0.8s ease',
      border: '1px solid rgba(255,217,102,0.2)',
    },
    ctaTitle: {
      fontSize: '1.6rem',
      fontWeight: 'bold',
      color: '#FFFFFF',
      marginBottom: '1rem',
    },
    ctaText: {
      color: silver,
      marginBottom: '2rem',
      fontSize: '1rem',
    },
    ctaButton: {
      display: 'inline-block',
      backgroundColor: gold,
      color: deepBlue,
      padding: '0.8rem 2rem',
      borderRadius: '50px',
      textDecoration: 'none',
      fontWeight: 'bold',
      transition: 'transform 0.2s, box-shadow 0.2s',
    },
  };

  const animationStyles = `
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      background-color: ${deepBlue};
      margin: 0;
      padding: 0;
    }
    html {
      font-size: 90%;
    }
    @keyframes fadeInLeft {
      from { opacity: 0; transform: translateX(-30px); }
      to { opacity: 1; transform: translateX(0); }
    }
    @keyframes fadeInRight {
      from { opacity: 0; transform: translateX(30px); }
      to { opacity: 1; transform: translateX(0); }
    }
    @keyframes fadeInDown {
      from { opacity: 0; transform: translateY(-30px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes pulseGlow {
      0% { opacity: 0.4; width: 80px; }
      50% { opacity: 1; width: 120px; }
      100% { opacity: 0.4; width: 80px; }
    }
    .timeline-item.visible,
    .value-item.visible,
    .cta-section.visible {
      opacity: 1 !important;
      transform: translateX(0) translateY(0) !important;
    }
    .timeline-item:hover .timeline-icon {
      transform: scale(1.1);
      background: ${gold};
      color: ${deepBlue};
      box-shadow: 0 0 15px ${gold};
    }
    .value-item:hover .value-icon {
      transform: translateX(5px);
    }
    .value-item:hover {
      border-bottom-color: ${gold};
    }
    .btn-primary:hover, .cta-button:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(0,0,0,0.2);
    }
    .hero-image:hover {
      transform: scale(1.02);
    }
  `;

  return (
    <div ref={containerRef} style={styles.pageWrapper}>
      <style>{animationStyles}</style>
      <canvas ref={canvasRef} style={styles.canvas} />
      <div style={styles.content}>
        <h1 style={styles.pageHeading}>About Us</h1>

        <div style={styles.hero}>
          <div style={styles.heroLeft}>
            <div style={styles.heroBadge}>Trusted Since 2005</div>
            <h1 style={styles.heroTitle}>Your Partner in<br />Septic Excellence</h1>
            <p style={styles.heroText}>
              We combine decades of experience with modern technology to deliver reliable,
              eco‑friendly septic services. From routine maintenance to emergency repairs,
              we're here to protect your home and the environment.
            </p>
            <Link to="/contact" style={styles.btnPrimary} className="btn-primary">
              Get a Free Quote
            </Link>
          </div>
          <div style={styles.heroRight}>
            <img
              src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR2hN1FA9eo1lcS0jyTI3yrbPxZtY4-d6VUaQ&s"
              alt="Professional septic service"
              style={styles.heroImage}
              className="hero-image"
            />
          </div>
        </div>

        <div style={styles.separator}>
          <div style={styles.separatorLine} />
          <div style={styles.separatorText}>
            “Reliability you can count on, care you can feel”
          </div>
        </div>

        <div style={styles.timelineSection}>
          <h2 style={styles.sectionTitle}>Our Journey</h2>
          <div className="timeline-container" style={styles.timelineContainer}>
            <div ref={timelineLineRef} style={styles.timelineLine} />
            {timelineItems.map((item, idx) => (
              <div
                key={idx}
                ref={(el) => addTimelineRef(el, idx)}
                className="timeline-item"
                style={{ ...styles.timelineItem, transitionDelay: `${idx * 0.1}s` }}
              >
                <div style={styles.timelineIcon}>{item.icon}</div>
                <div style={styles.timelineYear}>{item.year}</div>
                <div style={styles.timelineTitle}>{item.title}</div>
                <div style={styles.timelineDesc}>{item.desc}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={styles.valuesSection}>
          <h2 style={styles.sectionTitle}>Our Values</h2>
          {values.map((value, idx) => (
            <div
              key={idx}
              ref={(el) => addValueRef(el, idx)}
              className="value-item"
              style={{ ...styles.valueItem, transitionDelay: `${idx * 0.1}s` }}
            >
              <div style={styles.valueIcon} className="value-icon">{value.icon}</div>
              <div style={styles.valueContent}>
                <div style={styles.valueTitle}>{value.title}</div>
                <div style={styles.valueDesc}>{value.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div
          ref={ctaRef}
          className="cta-section"
          style={styles.ctaSection}
        >
          <h3 style={styles.ctaTitle}>Ready to experience the Varahi difference?</h3>
          <p style={styles.ctaText}>Contact us today for a free consultation and honest quote.</p>
          <Link to="/contact" style={styles.ctaButton} className="cta-button">
            Get Started
          </Link>
        </div>
      </div>
    </div>
  );
};

export default About;